import {
  forwardRef,
  createRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from 'react';
import { DeleteTwoTone, DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  Button,
  Row,
  Col,
  Input,
  Checkbox,
  Typography,
  Modal,
  Tooltip,
  Form,
  type FormInstance,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { getNextOrder } from '../utils/orderUtil';
import { isNotValidNumber } from '../utils/formUtils';
import { FormItemWrapper } from '../../form-components';
import { PriceOption, PricePart } from '../types';
import { cloneDeep } from 'lodash-es';
import { calcEstimatedPriceByCost } from '../utils/productUtils';
import { useModalToggle } from '../hooks';
import minPriceOnImg from '../../assets/images/min-price-on.png';
import minPriceOffImg from '../../assets/images/min-price-off.png';
import customQtyOnImg from '../../assets/images/custom-qty-on.png';
import customQtyOffImg from '../../assets/images/custom-qty-off.png';
import PriceOptionForm, { createTieredPriceItem } from './PriceOptionForm';
import { arePricePartsEqual } from '../utils/pricePartComparators';
import { css } from '@emotion/react';
import { cn } from '@arcsite/utils';

const createPriceOption = (
  price: number | null = null,
  cost: number | null = null,
  order = 0
) => {
  return {
    name: null,
    uuid: uuidv4(),
    price,
    cost,
    use_custom_quantity: false,
    custom_quantity_unit: null,

    use_minimum_price: false,
    minimum_price: null,

    use_minimum_cost: false,
    minimum_cost: null,

    use_tiered_price: false,
    tiered_price_method: null,
    tiered_prices: null,

    order,
  };
};

// TODO: 多参数函数尽量优化成1-2个参数，参数可以使用对象结构；这样可以保证 caller 的便利与安全性
const createPricePart = (
  name: string | null = null,
  price: number | null = null,
  cost: number | null = null,
  order = 0
) => {
  if (typeof price === 'string') {
    if (price === '') {
      price = null;
    } else {
      price = parseFloat(price);
    }
  }

  return {
    name,
    uuid: uuidv4(),
    optional: false,
    mandatory: false,
    price_options: [createPriceOption(price, cost)],
    order,
  };
};

type PricePartsFormData = {
  parts: PricePart[];
};

type PricePartsFormProps = {
  priceParts: PricePart[];
  originalPriceParts?: PricePart[];
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  onSubmit?: (price_parts: PricePart[]) => void | Promise<void>;
  renderCalculatedUnitPriceList?: (cost: number) => React.ReactNode;
  scene?: 'enterprise' | 'user-site';
  onValuesChange?: (
    changedValues: Partial<PricePart>,
    allValues: PricePartsFormData
  ) => void;
};

export interface PricePartFormRef extends FormInstance {
  submit: () => Promise<PricePartsFormData>;
}

const PricePartsForm = forwardRef<PricePartFormRef, PricePartsFormProps>(
  (props, formRef) => {
    const [form] = Form.useForm<PricePartsFormData>();
    const pricingMethodByCost = props.pricingMethodByCost;

    const addPriceOptionExtras = useCallback((priceOption: PriceOption) => {
      // Ensure every price option has a UUID
      if (!priceOption.uuid) {
        priceOption.uuid = uuidv4();
      }

      if (priceOption.use_tiered_price) {
        // Always ensure tiered_price_method has a default value when enabled
        if (!(priceOption as any).tiered_price_method) {
          (priceOption as any).tiered_price_method = 'MULTIPLY';
        }

        // Only create default tiered prices if they don't exist or are empty
        // This preserves existing tiered price data from the advanced modal
        if (
          !priceOption.tiered_prices ||
          priceOption.tiered_prices.length === 0
        ) {
          priceOption.tiered_prices = [
            createTieredPriceItem(10, priceOption.price),
            createTieredPriceItem(null, priceOption.price),
          ];
        }
      } else {
        // If tiered pricing is disabled, reset tiered pricing fields
        priceOption.tiered_prices = null;
        (priceOption as any).tiered_price_method = null;
      }
    }, []);

    const removePriceOptionExtras = useCallback((priceOption: PriceOption) => {
      if (!priceOption.use_minimum_price) {
        priceOption.minimum_price = null;
      }

      if (!priceOption.use_tiered_price) {
        priceOption.tiered_prices = null;
        (priceOption as any).tiered_price_method = null;
      }

      if (!priceOption.use_custom_quantity) {
        priceOption.custom_quantity_unit = null;
      }
    }, []);

    const updatePriceOptions = useCallback(
      (parts: PricePart[], method: (priceOption: PriceOption) => void) => {
        const priceParts = cloneDeep(parts);
        if (!priceParts || priceParts.length <= 0) {
          return;
        }

        for (const pricePart of priceParts) {
          const options = pricePart.price_options;
          if (!options || options.length < 1) {
            continue;
          }

          for (const priceOption of options) {
            method(priceOption);
          }
        }

        return priceParts;
      },
      []
    );

    // Destructure props to fix eslint warning
    const { onSubmit } = props;

    const handleFinish = useCallback(
      (values: PricePartsFormData) => {
        // Guard against undefined values
        if (!values || !values.parts) {
          console.error('Form values are undefined or missing parts');
          return;
        }

        const parts = updatePriceOptions(values.parts, removePriceOptionExtras);

        // Call the onSubmit prop if provided
        if (onSubmit) {
          try {
            const result = onSubmit(parts!);
            if (result instanceof Promise) {
              result.catch(error => {
                console.error('Price parts submission failed:', error);
              });
            }
          } catch (error) {
            console.error('Price parts submission failed:', error);
          }
        }
      },
      [removePriceOptionExtras, updatePriceOptions, onSubmit]
    );

    // Expose the form instance through ref with a submit method
    useImperativeHandle(
      formRef,
      () => ({
        ...form,
        submit: async () => {
          const values = await form.validateFields();
          handleFinish(values);
          return values;
        },
      }),
      [form, handleFinish]
    );

    const initialValues: PricePartsFormData = {
      parts: props.priceParts,
    };

    return (
      <Form
        css={css`
          .ant-form-item-control-input {
            min-height: 24px !important;
          }
        `}
        form={form}
        initialValues={initialValues}
        onFinish={handleFinish}
        onValuesChange={props.onValuesChange}
      >
        <ProductPricePartList
          pricingMethodByCost={pricingMethodByCost}
          currencySymbol={props.currencySymbol}
          defaultGrossMargin={props.defaultGrossMargin}
          addPriceOptionExtras={addPriceOptionExtras}
          originalPriceParts={props.originalPriceParts}
          renderCalculatedUnitPriceList={props.renderCalculatedUnitPriceList}
          scene={props.scene}
        />
      </Form>
    );
  }
);

type ProductPricePartListProps = {
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  addPriceOptionExtras: (priceOption: PriceOption) => void;
  originalPriceParts?: PricePart[];
  renderCalculatedUnitPriceList?: (cost: number) => React.ReactNode;
  scene?: 'enterprise' | 'user-site';
};

const ProductPricePartList = (props: ProductPricePartListProps) => {
  const {
    pricingMethodByCost,
    currencySymbol,
    defaultGrossMargin,
    addPriceOptionExtras,
    originalPriceParts,
    renderCalculatedUnitPriceList,
    scene,
  } = props;

  const handleDelete = (index: number, remove: (index: number) => void) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Are you sure to delete the ${
        pricingMethodByCost ? 'cost ' : 'price '
      } part?`,
      onOk: () => {
        remove(index);
      },
    });
  };

  return (
    <div>
      <Form.List name="parts">
        {(fields, { add, remove, move }) => (
          <div>
            <div style={{ overflow: 'hide auto' }}>
              {fields.map((field, index) => (
                <ProductPricePart
                  key={field.key}
                  pricingMethodByCost={pricingMethodByCost}
                  currencySymbol={currencySymbol}
                  defaultGrossMargin={defaultGrossMargin}
                  addPriceOptionExtras={addPriceOptionExtras}
                  field={field}
                  index={index}
                  originalPricePart={originalPriceParts?.[index]}
                  renderCalculatedUnitPriceList={renderCalculatedUnitPriceList}
                  scene={scene}
                  onDelete={() => handleDelete(index, remove)}
                  onLiftUp={() => {
                    if (index > 0) {
                      move(index, index - 1);
                    }
                  }}
                  onLiftDown={() => {
                    if (index < fields.length - 1) {
                      move(index, index + 1);
                    }
                  }}
                  canLiftUp={index > 0}
                  canLiftDown={index < fields.length - 1}
                />
              ))}
            </div>
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Button
                type="dashed"
                style={{ display: 'inline-block' }}
                onClick={() =>
                  add(
                    createPricePart(
                      null,
                      null,
                      null,
                      getNextOrder(fields.map((_, i) => ({ order: i })))
                    )
                  )
                }
              >
                + Add Price Part
              </Button>
            </div>
          </div>
        )}
      </Form.List>
    </div>
  );
};

type ProductPricePartProps = {
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  addPriceOptionExtras: (priceOption: PriceOption) => void;
  field: any;
  index: number;
  originalPricePart?: PricePart;
  renderCalculatedUnitPriceList?: (cost: number) => React.ReactNode;
  onDelete: () => void;
  onLiftUp: () => void;
  onLiftDown: () => void;
  canLiftUp: boolean;
  canLiftDown: boolean;
  scene?: 'enterprise' | 'user-site';
};

const ProductPricePart = (props: ProductPricePartProps) => {
  const {
    field,
    index,
    originalPricePart,
    renderCalculatedUnitPriceList,
    pricingMethodByCost,
    currencySymbol,
    defaultGrossMargin,
    addPriceOptionExtras,
    onDelete,
    onLiftUp,
    onLiftDown,
    canLiftUp,
    canLiftDown,
    scene,
  } = props;

  const form = Form.useFormInstance();

  // 监听当前价格部件的值变化
  const currentPricePart = Form.useWatch(['parts', index], form);

  // 检测是否有变化
  const hasChanged = useMemo(() => {
    if (scene !== 'enterprise') return false;
    if (!originalPricePart && !currentPricePart) return false;
    return !arePricePartsEqual(currentPricePart, originalPricePart);
  }, [currentPricePart, originalPricePart, scene]);

  return (
    <div
      className={cn(
        'border-[1px] border-solid',
        hasChanged && 'border-overridden'
      )}
      style={{
        borderRadius: '5px',
        marginBottom: '5px',
        backgroundColor: 'lightgray',
      }}
    >
      <Row align="middle" style={{ padding: '5px 5px' }}>
        <Col span={6}>
          <Typography.Text strong>Price Part</Typography.Text>
        </Col>
        <Col span={12}>
          <FormItemWrapper
            name={[props.field.name, 'name']}
            rules={[{ required: true, message: 'Price part name is required' }]}
          >
            <Input
              size="small"
              style={{ width: '100%' }}
              placeholder="Price Name"
            />
          </FormItemWrapper>
        </Col>
        <Col offset={0} span={6}>
          <div className="flex items-center justify-end">
            <Button
              size="small"
              disabled={!canLiftUp}
              onClick={onLiftUp}
              icon={<UpOutlined />}
            />
            <Button
              size="small"
              disabled={!canLiftDown}
              onClick={onLiftDown}
              icon={<DownOutlined />}
            />
            <Button size="small" onClick={onDelete}>
              <DeleteTwoTone twoToneColor="red" />
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <PriceOptionsList
          partField={field}
          partIndex={index}
          pricingMethodByCost={pricingMethodByCost}
          currencySymbol={currencySymbol}
          defaultGrossMargin={defaultGrossMargin}
          addPriceOptionExtras={addPriceOptionExtras}
          renderCalculatedUnitPriceList={renderCalculatedUnitPriceList}
        />
      </Row>
      <Row className="px-1 pt-1">
        <FormItemWrapper
          name={[field.name, 'optional']}
          valuePropName="checked"
        >
          <Checkbox style={{ fontWeight: 'unset', fontSize: '12px' }}>
            <Tooltip title="An optional price part can be excluded from the product. And its price is not counted into the total cost once excluded.">
              This part is optional to the product
            </Tooltip>
          </Checkbox>
        </FormItemWrapper>
      </Row>
      <Row className="px-1 pb-1">
        <FormItemWrapper
          name={[field.name, 'mandatory']}
          valuePropName="checked"
        >
          <Checkbox style={{ fontWeight: 'unset', fontSize: '12px' }}>
            <Tooltip title="Users must enter all 'required' information in app before generating price proposal.">
              Users are required to select one option in APP
            </Tooltip>
          </Checkbox>
        </FormItemWrapper>
      </Row>
    </div>
  );
};

type PriceOptionsListProps = {
  partField: any;
  partIndex: number;
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  addPriceOptionExtras: (priceOption: PriceOption) => void;
  renderCalculatedUnitPriceList?: (cost: number) => React.ReactNode;
};

const PriceOptionsList = (props: PriceOptionsListProps) => {
  const {
    partField,
    partIndex,
    pricingMethodByCost,
    currencySymbol,
    defaultGrossMargin,
    addPriceOptionExtras,
    renderCalculatedUnitPriceList,
  } = props;

  const handleDelete = (index: number, remove: (index: number) => void) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Are you sure to delete the ${
        pricingMethodByCost ? 'cost' : 'price'
      } option?`,
      onOk: () => {
        remove(index);
      },
    });
  };

  return (
    <Form.List name={[partField.name, 'price_options']}>
      {(fields, { add, remove, move }) => (
        <div className="bg-[#fff] w-full ">
          <Row className="text-12 px-4 pt-1 font-semibold text-black-55">
            <Col className="w-[312px]">
              <div>Option Name</div>
            </Col>
            <Col className="w-[175px]">
              <div>Unit Cost</div>
            </Col>
            <Col className="w-116">
              <div>Unit Price</div>
            </Col>
          </Row>
          <Row className="mb-0">
            {fields.map((field, index) => (
              <PriceOptionItem
                key={field.key}
                pricingMethodByCost={pricingMethodByCost}
                currencySymbol={currencySymbol}
                defaultGrossMargin={defaultGrossMargin}
                addPriceOptionExtras={addPriceOptionExtras}
                renderCalculatedUnitPriceList={renderCalculatedUnitPriceList}
                onDelete={() => handleDelete(index, remove)}
                field={field}
                index={index}
                partIndex={partIndex}
                canDelete={fields.length > 1}
                canLiftUp={index > 0}
                canLiftDown={index < fields.length - 1}
                onLiftUp={() => {
                  if (index > 0) {
                    move(index, index - 1);
                  }
                }}
                onLiftDown={() => {
                  if (index < fields.length - 1) {
                    move(index, index + 1);
                  }
                }}
              />
            ))}
          </Row>
          <Row justify="center" style={{ padding: '0 0 5px' }}>
            <Button
              size="small"
              type="dashed"
              onClick={() =>
                add(
                  createPriceOption(
                    null,
                    null,
                    getNextOrder(fields.map((_, i) => ({ order: i })))
                  )
                )
              }
            >
              + Add Option
            </Button>
          </Row>
        </div>
      )}
    </Form.List>
  );
};

type PriceOptionItemProps = {
  partIndex: number;
  index: number;
  field: any;
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  addPriceOptionExtras: (priceOption: PriceOption) => void;
  onDelete: () => void;
  onLiftUp: () => void;
  onLiftDown: () => void;
  canLiftDown: boolean;
  canDelete: boolean;
  canLiftUp: boolean;
  renderCalculatedUnitPriceList?: (cost: number) => React.ReactNode;
};

const PriceOptionItem = (props: PriceOptionItemProps) => {
  const [showAdvancedModal, toggleAdvancedModal] = useModalToggle();
  const {
    partIndex,
    index,
    field,
    pricingMethodByCost,
    currencySymbol,
    defaultGrossMargin,
    addPriceOptionExtras,
    renderCalculatedUnitPriceList,
  } = props;

  const formRef: any = createRef();
  const form = Form.useFormInstance();

  // Watch specific form values for this option
  const option = Form.useWatch(
    ['parts', partIndex, 'price_options', index],
    form
  );
  const useTieredPrice = option?.use_tiered_price || false;
  const useMinPrice = option?.use_minimum_price || false;
  const useCustomQty = option?.use_custom_quantity || false;
  const optionName = form.getFieldValue([
    'parts',
    partIndex,
    'price_options',
    index,
    'name',
  ]);

  const handleAdvancedOk = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (formRef.current) {
      formRef.current
        .submitForm()
        .then(() => {
          toggleAdvancedModal();
        })
        .catch((error: any) => {
          console.log('Advanced options validation failed:', error);
        });
    }
  };

  const handleAdvancedSubmit = (option: PriceOption) => {
    // Update the form with the new option values
    const currentValues = form.getFieldsValue();
    const newValues = { ...currentValues };
    if (
      newValues.parts &&
      newValues.parts[partIndex] &&
      newValues.parts[partIndex].price_options
    ) {
      // Preserve the current name and other basic fields from the main form
      const currentOption = newValues.parts[partIndex].price_options[index];
      const updatedOption = {
        ...option,
        // Preserve fields that are edited in the main form
        name: currentOption?.name || option.name || '',
        price: option.price !== undefined ? option.price : currentOption?.price,
        cost:
          (option as any).cost !== undefined
            ? (option as any).cost
            : (currentOption as any)?.cost,
      };

      // Apply the addPriceOptionExtras logic to ensure proper initialization
      // especially for tiered pricing when it's enabled
      addPriceOptionExtras(updatedOption);

      newValues.parts[partIndex].price_options[index] = updatedOption;
      form.setFieldsValue(newValues);
    }
  };

  return (
    <div className="my-1 ml-4">
      <Row gutter={8} className="items-stretch">
        <Col className="w-[312px]">
          <FormItemWrapper
            name={[field.name, 'name']}
            rules={[
              {
                validator: (_, value) => {
                  const values = form.getFieldsValue();
                  const currentPartOptions =
                    values.parts?.[partIndex]?.price_options;

                  // If there are multiple options in this price part, name is required
                  if (currentPartOptions && currentPartOptions.length > 1) {
                    if (!value || value.trim() === '') {
                      return Promise.reject(new Error('Name required'));
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input size="small" placeholder="Option" />
          </FormItemWrapper>
        </Col>
        <Col className="w-[150px]">
          <FormItemWrapper
            name={[field.name, 'cost']}
            rules={[
              {
                validator: (_, value) => {
                  const values = form.getFieldsValue();
                  const option =
                    values.parts?.[partIndex]?.price_options?.[index];

                  // If tiered pricing is enabled, cost is not required
                  if (option?.use_tiered_price) {
                    return Promise.resolve();
                  }

                  if (pricingMethodByCost) {
                    // CALCULATED PRICING METHOD: Cost is always required
                    if (isNotValidNumber(value)) {
                      return Promise.reject(new Error('Cost is required'));
                    }
                  } else {
                    // INPUT PRICING METHOD: Cost is optional (price is required instead)
                    // No validation needed for cost field in INPUT pricing method
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              addonBefore={currencySymbol}
              size="small"
              style={{ width: '100%', top: '0' }}
            />
          </FormItemWrapper>
        </Col>
        <Col className="text-center">
          <Row className="flex items-center h-3">
            {useMinPrice ? (
              <Tooltip title="Minimum Price applied">
                <img
                  style={{ height: '8px', display: 'block' }}
                  src={minPriceOnImg}
                  alt="min price on"
                />
              </Tooltip>
            ) : (
              <Tooltip title="Minimum Price not set">
                <img
                  style={{ height: '8px', display: 'block' }}
                  src={minPriceOffImg}
                  alt="min price off"
                />
              </Tooltip>
            )}
          </Row>
          <Row className="flex items-center h-3">
            {useCustomQty ? (
              <Tooltip title="Use Custom Quantity">
                <img
                  style={{ height: '8px', display: 'block' }}
                  src={customQtyOnImg}
                  alt="custom qty on"
                />
              </Tooltip>
            ) : (
              <Tooltip title="Use Product Quantity">
                <img
                  style={{ height: '8px', display: 'block' }}
                  src={customQtyOffImg}
                  alt="custom qty off"
                />
              </Tooltip>
            )}
          </Row>
        </Col>
        <Col className="w-[150px]">
          {(() => {
            if (renderCalculatedUnitPriceList) {
              return (
                <Tooltip
                  title={renderCalculatedUnitPriceList(option?.cost || 0)}
                  overlayClassName="w-[500px]"
                  overlayInnerStyle={{
                    width: 500,
                    padding: 20,
                  }}
                  color="white"
                >
                  <Button type="link" size="small" className="underline">
                    Varies by Company
                  </Button>
                </Tooltip>
              );
            }
            if (useTieredPrice) {
              return (
                <Input
                  disabled
                  size="small"
                  value="Tiered"
                  className="text-center"
                />
              );
            }

            if (pricingMethodByCost) {
              const cost = option?.cost || 0;
              const calcEstimatedPrice = calcEstimatedPriceByCost({
                cost,
                defaultGrossMargin: defaultGrossMargin || 0,
              });
              return (
                <Input
                  disabled
                  addonBefore={currencySymbol}
                  value={calcEstimatedPrice || ''}
                  size="small"
                />
              );
            }

            return (
              <FormItemWrapper
                name={[field.name, 'price']}
                rules={[
                  {
                    validator: (_, value) => {
                      // If tiered pricing is enabled, price validation is not needed
                      if (useTieredPrice) {
                        return Promise.resolve();
                      }

                      // If cost-based pricing, price field is disabled and calculated
                      if (pricingMethodByCost) {
                        return Promise.resolve();
                      }

                      // INPUT PRICING METHOD: Price is required
                      if (isNotValidNumber(value)) {
                        return Promise.reject(new Error('Price required'));
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  addonBefore={currencySymbol}
                  size="small"
                />
              </FormItemWrapper>
            );
          })()}
        </Col>
        <Col className="ml-2.5">
          <div className="flex items-center">
            <Button
              size="small"
              disabled={!props.canLiftUp}
              icon={<UpOutlined />}
              onClick={props.onLiftUp}
              style={{ padding: '0 4px' }}
            />
            <Button
              size="small"
              disabled={!props.canLiftDown}
              icon={<DownOutlined />}
              onClick={props.onLiftDown}
              style={{ padding: '0 4px' }}
            />
            {props.canDelete && (
              <Button size="small" onClick={props.onDelete}>
                <DeleteTwoTone twoToneColor="red" />
              </Button>
            )}
          </div>
        </Col>
        <Col>
          <Button
            size="small"
            onClick={() => toggleAdvancedModal()}
            style={{ color: '#1890ff' }}
          >
            Advanced
          </Button>
          <Modal
            zIndex={2000}
            title={`Option Detail${optionName ? ` - ${optionName}` : ''}`}
            open={showAdvancedModal}
            onOk={handleAdvancedOk}
            onCancel={() => toggleAdvancedModal()}
            width={520}
          >
            <PriceOptionForm
              pricingMethodByCost={pricingMethodByCost}
              currencySymbol={currencySymbol}
              defaultGrossMargin={defaultGrossMargin}
              option={option || {}}
              onSubmit={handleAdvancedSubmit}
              ref={formRef}
            />
          </Modal>
        </Col>
      </Row>
    </div>
  );
};

export { PricePartsForm, createPricePart };
export type { PricePartsFormProps };
